// The kit's building blocks: sections (Card), rows, buttons, discs, medallions, bars, segmented controls, fields.
// Their finish is Fire & Slate Rev. 11 (kit.css): open sections and rows, rims only on real controls.
import { forwardRef, type ButtonHTMLAttributes, type HTMLAttributes, type InputHTMLAttributes, type ReactNode } from "react";
import { IconChevronDown, IconChevronRight } from "./icons";

const cx = (...a: (string | number | false | null | undefined)[]) => a.filter((x): x is string => typeof x === "string" && x !== "").join(" ");

// ------------------------------------------------------------------ cards

/** A section. Fire & Slate Rev. 11 (kit.css "open sections"): an open full-width band on the shared slate that ends on the
 *  major gold rail — no box, sidewalls or filigree. Nested in another section, a sheet or a dialog it ends on a thin rule;
 *  as a sheet or dialog it keeps a rim (modal separation). */
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** a closed collapsible section (kept for callers; the open finish needs no size variant) */
  small?: boolean;
  /** kept for callers: sections carry no corner ornament any more */
  plain?: boolean;
  /** no padding (hero sections lay themselves out) */
  bare?: boolean;
}
export const Card = ({ small, plain, bare, className, children, ...rest }: CardProps) => (
  <section className={cx("frame", small && "small", plain && "plain", !bare && "card", className)} {...rest}>
    {children}
  </section>
);

export const CardHead = ({ title, aside, asideCaps, badge, onToggle, open, children }: {
  title: ReactNode; aside?: ReactNode; asideCaps?: boolean; badge?: number; onToggle?: () => void; open?: boolean; children?: ReactNode;
}) => (
  // a collapsible section's whole header toggles it (the chevron disc is the keyboard / screen-reader control)
  <header className={cx("card-head", onToggle && "tog")} onClick={onToggle ? (e) => { if (!(e.target as HTMLElement).closest("button, a, input")) onToggle(); } : undefined}>
    <h2>
      {title}
      {badge ? <span className="badge inline">{badge}</span> : null}
    </h2>
    {children}
    {aside !== undefined && <span className={cx("aside", asideCaps && "caps")}>{aside}</span>}
    {onToggle && (
      <Disc size="xs" label={open ? "Collapse" : "Expand"} onClick={onToggle} aria-expanded={open}>
        {open ? <IconChevronDown /> : <IconChevronRight />}
      </Disc>
    )}
  </header>
);

// ------------------------------------------------------------------ rows

export interface RowProps extends HTMLAttributes<HTMLElement> {
  as?: "div" | "button";
  alert?: boolean;
  unread?: boolean;
  sub?: boolean;
  disabled?: boolean;
}
export const Row = ({ as = "div", alert, unread, sub, className, children, ...rest }: RowProps) => {
  const cls = cx("row", alert && "alert", unread && "unread", sub && "sub", className);
  if (as === "button") return <button type="button" className={cls} {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}>{children}</button>;
  return <div className={cls} {...rest}>{children}</div>;
};

export const RowText = ({ eyebrow, line, meta }: { eyebrow?: ReactNode; line: ReactNode; meta?: ReactNode }) => (
  <div className="txt">
    {eyebrow && <div className="eyebrow">{eyebrow}</div>}
    <div className="line">{line}</div>
    {meta && <div className="meta">{meta}</div>}
  </div>
);

// ------------------------------------------------------------------ buttons & discs

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  kind?: "steel" | "crimson" | "gold" | "ghost";
  size?: "sm" | "md" | "lg";
  wide?: boolean;
  badge?: number;
  busy?: boolean;
}
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { kind = "steel", size = "lg", wide, badge, busy, className, children, disabled, type = "button", ...rest }, ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx("btn", kind === "crimson" && "crimson", kind === "gold" && "goldb", kind === "ghost" && "ghost", size !== "lg" && size, wide && "wide", className)}
      disabled={disabled || busy}
      {...rest}
    >
      {busy ? <span className="spin" aria-hidden="true" /> : null}
      {children}
      {badge ? <span className="badge">{badge}</span> : null}
    </button>
  );
});

export interface DiscProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  size?: "lg" | "sm" | "xs";
  on?: boolean;
  badge?: number;
  label: string;
}
export const Disc = ({ size = "lg", on, badge, label, className, children, ...rest }: DiscProps) => (
  <button type="button" className={cx("disc", size !== "lg" && size, on && "on", className)} aria-label={label} title={label} {...rest}>
    {children}
    {badge ? <span className="badge">{badge}</span> : null}
  </button>
);

// ------------------------------------------------------------------ medallions

export interface MedallionProps extends HTMLAttributes<HTMLSpanElement> {
  src?: string;
  alt?: string;
  /** a letter (player initial) when there is no picture */
  initial?: string;
  size?: number | string;
  glow?: boolean | "crim" | "ember";
  dim?: boolean;
}
export const Medallion = ({ src, alt = "", initial, size, glow, dim, className, style, children, ...rest }: MedallionProps) => (
  <span
    className={cx("medal", glow && "glow", glow === "crim" && "crim", glow === "ember" && "ember", initial && !src && "initial", dim && "dim", className)}
    style={size !== undefined ? { ...style, ["--s" as string]: typeof size === "number" ? `calc(var(--u) * ${size})` : size } : style}
    {...rest}
  >
    {src ? <img src={src} alt={alt} loading="lazy" decoding="async" /> : initial ? initial.slice(0, 1).toUpperCase() : children}
  </span>
);

// ------------------------------------------------------------------ bars & bands

export const Bar = ({ value, color, thick, diamond, className }: { value: number; color?: "gold" | "green" | "crim"; thick?: boolean; diamond?: boolean; className?: string }) => (
  <div className={cx("bar", color, thick && "thick", diamond && "gold-track", className)} role="progressbar" aria-valuenow={Math.round(value * 100)} aria-valuemin={0} aria-valuemax={100}>
    <i style={{ ["--w" as string]: `${Math.max(0, Math.min(100, value * 100))}%` }} />
  </div>
);

export const StatBand = ({ stats }: { stats: { label: ReactNode; value: ReactNode; tone?: "crim" | "green"; small?: ReactNode }[] }) => (
  <div className="statband">
    {stats.map((s, i) => (
      <div key={i}>
        <div className="lbl">{s.label}</div>
        <div className={cx("val", s.tone)}>
          {s.value}
          {s.small ? <small> {s.small}</small> : null}
        </div>
      </div>
    ))}
  </div>
);

export interface HeroProps {
  art?: string;
  artWidth?: string;
  artPos?: string;
  full?: boolean;
  title: ReactNode;
  lines?: ReactNode[];
  link?: ReactNode;
  stats?: { label: ReactNode; value: ReactNode; tone?: "crim" | "green"; small?: ReactNode }[];
  children?: ReactNode;
  className?: string;
}
export const HeroCard = ({ art, artWidth, artPos, full, title, lines, link, stats, children, className }: HeroProps) => (
  <Card bare className={cx("hero", full && "full", className)}>
    {art && (
      <div className="art" style={{ ["--art-w" as string]: artWidth, ["--art-pos" as string]: artPos }}>
        <img src={art} alt="" decoding="async" />
      </div>
    )}
    <div className="body">
      <h2>{title}</h2>
      {lines?.map((l, i) => <p key={i}>{l}</p>)}
      {link}
      {children}
    </div>
    {stats && <StatBand stats={stats} />}
  </Card>
);

// ------------------------------------------------------------------ controls

/** The primary tab strip (Guild · Diplomacy · Manage, Store's storefronts…): adjoining rectangular tabs ending on a rail;
 *  the selected tab is a weak local light source. `Tabs` is the quieter subtab strip under it. */
export const Seg = <T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode; badge?: number }[] }) => (
  <div className={cx("seg", options.length >= 4 && "n4")} role="tablist">
    {options.map((o) => (
      <button key={o.value} type="button" role="tab" aria-selected={o.value === value} className={o.value === value ? "on" : undefined} onClick={() => onChange(o.value)}>
        {o.label}
        {o.badge ? <span className="badge inline">{o.badge}</span> : null}
      </button>
    ))}
  </div>
);

export const Tabs = <T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: { value: T; label: ReactNode }[] }) => (
  <div className="tabs" role="tablist">
    {options.map((o) => (
      <button key={o.value} type="button" role="tab" aria-selected={o.value === value} className={o.value === value ? "on" : undefined} onClick={() => onChange(o.value)}>
        {o.label}
      </button>
    ))}
  </div>
);

export const LinkButton = ({ caps, children, chevron = true, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { caps?: boolean; chevron?: boolean }) => (
  <button type="button" className={cx("link", caps && "caps")} {...rest}>
    {children}
    {chevron && <IconChevronRight />}
  </button>
);

export interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: ReactNode;
  error?: ReactNode;
}
export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field({ label, hint, error, id, className, ...rest }, ref) {
  const fid = id || (label ? "f-" + label.toLowerCase().replace(/[^a-z0-9]+/g, "-") : undefined);
  return (
    <div className="field">
      {label && <label htmlFor={fid}>{label}</label>}
      <input ref={ref} id={fid} className={cx("input", error ? "err" : undefined, className)} {...rest} />
      {error ? <div className="hint err">{error}</div> : hint ? <div className="hint">{hint}</div> : null}
    </div>
  );
});

export const Empty = ({ children }: { children: ReactNode }) => <div className="empty">{children}</div>;
export const Skeleton = ({ h }: { h?: number }) => <div className="skel" style={h ? { height: `calc(var(--u) * ${h})` } : undefined} aria-hidden="true" />;

export { cx };
